import React from 'react';

const PRIMARY       = '#E65100';
const PRIMARY_LIGHT = '#FF8A65';
const PRIMARY_DARK  = '#BF360C';
const TEXT_SEC      = '#6B4226';
const BG_GLOW       = 'rgba(230,81,0,0.08)';

export default function PageLoader({ label = 'Loading…', minHeight = 400 }) {
  return (
    <div style={{ ...S.wrap, minHeight }} aria-busy="true" aria-label={label}>
      <div style={S.inner}>

        {/* Orbital rings */}
        <div style={S.orbiterWrap}>
          {/* Glow halo */}
          <div style={S.glow} />

          {/* Outer ring */}
          <div style={S.ringOuter} />

          {/* Mid ring */}
          <div style={S.ringMid} />

          {/* Inner ring */}
          <div style={S.ringInner} />

          {/* Pulsing core */}
          <div style={S.core} />

          {/* Orbit dot — travels along outer ring */}
          <div style={S.orbitDot} />
        </div>

        {/* Bouncing dots */}
        <div style={S.dotsRow}>
          {[0, 0.18, 0.36].map((delay, i) => (
            <div
              key={i}
              style={{
                ...S.dot,
                animationDelay: `${delay}s`,
              }}
            />
          ))}
        </div>

        {/* Label */}
        {label && <p style={S.label}>{label}</p>}
      </div>
    </div>
  );
}

/* Inline-only small spinner for buttons / tight spaces */
export function MiniSpinner({ size = 16, color = '#fff' }) {
  return (
    <span
      style={{
        display:       'inline-block',
        width:         size,
        height:        size,
        borderRadius:  '50%',
        border:        `2px solid ${color}33`,
        borderTopColor: color,
        animation:     'pl-spin-cw 0.7s linear infinite',
        flexShrink:    0,
      }}
    />
  );
}

const S = {
  wrap: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    width:          '100%',
    animation:      'pl-fade-in 0.25s ease both',
  },

  inner: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            20,
  },

  /* ---- Orbiter container ---- */
  orbiterWrap: {
    position: 'relative',
    width:    88,
    height:   88,
  },

  glow: {
    position:     'absolute',
    inset:        -8,
    borderRadius: '50%',
    background:   `radial-gradient(circle, ${BG_GLOW} 0%, transparent 70%)`,
  },

  ringOuter: {
    position:     'absolute',
    inset:        0,
    borderRadius: '50%',
    border:       `3px solid transparent`,
    borderTopColor:   PRIMARY,
    borderRightColor: PRIMARY_LIGHT,
    animation:    'pl-spin-cw 1.3s cubic-bezier(0.6,0.2,0.4,0.8) infinite',
  },

  ringMid: {
    position:     'absolute',
    inset:        12,
    borderRadius: '50%',
    border:       `2.5px solid transparent`,
    borderTopColor:  PRIMARY_DARK,
    borderLeftColor: PRIMARY_LIGHT,
    animation:    'pl-spin-ccw 1s cubic-bezier(0.6,0.2,0.4,0.8) infinite',
  },

  ringInner: {
    position:     'absolute',
    inset:        24,
    borderRadius: '50%',
    border:       `2px solid transparent`,
    borderTopColor:    PRIMARY,
    borderBottomColor: PRIMARY_LIGHT,
    animation:    'pl-spin-cw 0.75s linear infinite',
  },

  core: {
    position:     'absolute',
    inset:        34,
    borderRadius: '50%',
    background:   `radial-gradient(circle at 35% 35%, ${PRIMARY_LIGHT}, ${PRIMARY})`,
    boxShadow:    `0 0 10px ${PRIMARY}66, 0 0 20px ${PRIMARY}33`,
    animation:    'pl-pulse 1.6s ease-in-out infinite',
  },

  /* Small dot orbiting the outer ring */
  orbitDot: {
    position:     'absolute',
    top:          -3,
    left:         '50%',
    marginLeft:   -5,
    width:        10,
    height:       10,
    borderRadius: '50%',
    background:   PRIMARY_LIGHT,
    boxShadow:    `0 0 6px ${PRIMARY_LIGHT}`,
    transformOrigin: '5px 47px',   /* radius = half of 88px + half of dot */
    animation:    'pl-spin-cw 1.3s cubic-bezier(0.6,0.2,0.4,0.8) infinite',
  },

  /* ---- Bouncing dots ---- */
  dotsRow: {
    display: 'flex',
    gap:     7,
  },

  dot: {
    width:           7,
    height:          7,
    borderRadius:    '50%',
    background:      PRIMARY,
    animation:       'pl-dot 1.2s ease-in-out infinite',
  },

  /* ---- Label ---- */
  label: {
    color:          TEXT_SEC,
    fontSize:       12,
    fontWeight:     600,
    letterSpacing:  '0.08em',
    textTransform:  'uppercase',
    margin:         0,
  },
};
